import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import ProductFilters from './ProductFilters';
import { Product, User, ProductType } from '../types';
import { productTypeKey, productTypeLabel } from '../utils/productType';

const ProductTable: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [users, setUsers] = useState<User[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [vintageFilter, setVintageFilter] = useState<string>('');
  const [alcoholMin, setAlcoholMin] = useState<string>('');
  const [alcoholMax, setAlcoholMax] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    brand: '',
    category: '',
    country: '',
    region: '',
    productType: 'all',
    product_type_id: '',
    category_id: '',
    subcategory_id: '',
    vintage: '',
    alcoholMin: '',
    alcoholMax: '',
    priceMin: '',  // Add this
    priceMax: '',  // Add this
    userId: 'all',
    grape_variety: '',
    characteristics: '',
    features: ''
  });
  
  // Add local state for price inputs
  const [priceInputs, setPriceInputs] = useState({
    priceMin: '',
    priceMax: ''
  });
  
  // Add ref for debounce timers
  const priceDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Get unique countries and years from products
  const countryOptions = Array.from(new Set(products.map((p: Product) => p.country).filter((c: string | undefined): c is string => Boolean(c)))).sort();
  const yearOptions = Array.from(new Set(products.map((p: Product) => p.vintage).filter((y: number | undefined): y is number => !!y))) as number[];
  yearOptions.sort((a: number, b: number) => b - a);

  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/product-types');
      setProductTypes(response.data);
    } catch (err: any) {
      console.error('Error fetching product types:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/products?';
      
      if (filters.product_type_id) url += `product_type_id=${filters.product_type_id}&`;
      if (filters.category_id) url += `category_id=${filters.category_id}&`;
      if (filters.subcategory_id) url += `subcategory_id=${filters.subcategory_id}&`;
      if (filters.userId !== 'all') url += `user_filter=${filters.userId}&`;
      if (filters.country) url += `country=${encodeURIComponent(filters.country)}&`;
      if (filters.vintage) url += `vintage=${filters.vintage}&`;
      if (filters.alcoholMin) url += `alcohol_content_min=${filters.alcoholMin}&`;
      if (filters.alcoholMax) url += `alcohol_content_max=${filters.alcoholMax}&`;
      if (filters.priceMin) url += `price_min=${filters.priceMin}&`;  // Add this
      if (filters.priceMax) url += `price_max=${filters.priceMax}&`;  // Add this
      if (filters.features) url += `features=${filters.features}&`;
      
      url = url.replace(/&$/, '');
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (err: any) {
      setError(extractErrorMessage(err));
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProducts();
    fetchUsers();
    fetchProductTypes();
  }, [fetchProducts, fetchUsers, fetchProductTypes]);

  useEffect(() => {
    // Apply name and user filtering
    let filtered = products;
    
    // Filter by user if not "all"
    if (userFilter !== 'all') {
      filtered = filtered.filter(product => 
        product.user_id?.toString() === userFilter
      );
    }
    
    // Filter by name/brand
    if (nameFilter.trim()) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
        (product.brand && product.brand.toLowerCase().includes(nameFilter.toLowerCase()))
      );
    }
    
    setFilteredProducts(filtered);
  }, [products, nameFilter, userFilter]);

  const deleteProduct = async (productId: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
      return;
    }

    try {
      await axios.delete(`/api/products/${productId}`);
      setProducts(products.filter(product => product.id !== productId));
    } catch (err: any) {
      setError(extractErrorMessage(err));
      console.error('Error deleting product:', err);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'Brak ceny';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  // This used to render the raw English name from the database ("wine"), while
  // the filter panel above it showed "Wino" for the same thing.
  const formatType = (productTypeId?: number) => productTypeLabel(productTypeId, productTypes);

  const getTypeClassName = (productTypeId?: number) => productTypeKey(productTypeId, productTypes);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // Handle specific fields as needed
    if (field === 'name') setNameFilter(value);
    else if (field === 'country') setCountryFilter(value);
    else if (field === 'vintage') setVintageFilter(value);
    else if (field === 'alcoholMin') setAlcoholMin(value);
    else if (field === 'alcoholMax') setAlcoholMax(value);
  };

  // Add debounced price filter handler
  const handlePriceInputChange = (field: 'priceMin' | 'priceMax', value: string) => {
    // Update local input state immediately
    setPriceInputs(prev => ({ ...prev, [field]: value }));
    
    // Clear existing timer
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    
    // Set new timer to update filters after 2 seconds
    priceDebounceTimer.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, [field]: value }));
    }, 2000);
  };
  
  // Handle blur event - apply filter immediately
  const handlePriceBlur = (field: 'priceMin' | 'priceMax') => {
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    setFilters(prev => ({ ...prev, [field]: priceInputs[field] }));
  };
  
  // Handle Enter key - apply filter immediately
  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'priceMin' | 'priceMax') => {
    if (e.key === 'Enter') {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
      setFilters(prev => ({ ...prev, [field]: priceInputs[field] }));
    }
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
    };
  }, []);
  
  // Initialize local price inputs from filters
  useEffect(() => {
    setPriceInputs({
      priceMin: filters.priceMin,
      priceMax: filters.priceMax
    });
  }, [filters.priceMin, filters.priceMax]);

  const handleProductTypeChange = (typeId: string) => {
    setFilterType(typeId);
    setFilters(prev => ({ 
      ...prev, 
      productType: typeId,
      product_type_id: typeId === 'all' ? '' : typeId,
      category_id: '',
      subcategory_id: '',
      priceMin: '',  // Reset price filters
      priceMax: ''
    }));
    setPriceInputs({ priceMin: '', priceMax: '' });
  };

  const handleUserFilterChange = (userId: string) => {
    setUserFilter(userId);
    setFilters(prev => ({ ...prev, userId }));
  };

  const resetAdvancedFilters = () => {
    setCountryFilter('');
    setVintageFilter('');
    setAlcoholMin('');
    setAlcoholMax('');
    setFilters(prev => ({
      ...prev,
      country: '',
      vintage: '',
      alcoholMin: '',
      alcoholMax: '',
      priceMin: '',  // Add this
      priceMax: '',  // Add this
      features: ''
    }));
    setPriceInputs({ priceMin: '', priceMax: '' });
  };

  if (loading) {
    return <div className="loading">Ładowanie produktów...</div>;
  }
  
  return (
    <div className="container">
      <div className="card">
        <div className="page-header">
          <div className="page-header__title">
            <h1>Baza Produktów Alkoholowych</h1>
            <span className="product-count">{filteredProducts.length}</span>
          </div>
          <div className="page-header__actions">
            {/* This link leaves the table for the card grid, so it said the
                opposite of what it does. */}
            <Link to="/" className="btn btn-secondary">
              Widok kafelków
            </Link>
            <Link to="/product-editor" className="btn btn-primary">
              Dodaj Produkt
            </Link>
          </div>
        </div>
        
        {/* Filtry */}
        <ProductFilters
          filters={{
            ...filters,
            priceMin: priceInputs.priceMin,
            priceMax: priceInputs.priceMax
          }}
          onChange={handleFilterChange}
          onProductTypeChange={handleProductTypeChange}
          onUserFilterChange={handleUserFilterChange}
          onPriceChange={handlePriceInputChange}
          onPriceBlur={handlePriceBlur}
          onPriceKeyDown={handlePriceKeyDown}
          showAdvancedFilters={showAdvanced}
          toggleAdvancedFilters={() => setShowAdvanced(v => !v)}
          resetFilters={resetAdvancedFilters}
          brandOptions={Array.from(new Set(products.map(p => p.brand).filter((b): b is string => !!b)))}
          categoryOptions={Array.from(new Set(products.map(p => p.category).filter((c): c is string => !!c))).map(c => ({ id: c, name: c }))}
          countryOptions={countryOptions}
          vintageOptions={yearOptions}
          userOptions={users.map(user => ({ id: user.id, username: user.username }))}
          /* .table-filters used to restyle the panel from the outside, which
             fought the panel's own rules. The panel styles itself now. */
          className=""
          productTypes={productTypes}
          resultCount={filteredProducts.length}
          totalCount={products.length}
        />

        {error && <div className="error-message">{error}</div>}

        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            {nameFilter ? (
              <div>
                <h3>Brak wyników wyszukiwania</h3>
                <p>Nie znaleziono produktów pasujących do "{nameFilter}"</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setNameFilter('')}
                >
                  Wyczyść filtr
                </button>
              </div>
            ) : (
              <div>
                <h3>Brak produktów</h3>
                <p>Dodaj pierwszy produkt do bazy danych!</p>
                <Link to="/product-editor" className="btn btn-primary">
                  Dodaj Produkt
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Typ</th>
                  <th>Marka</th>
                  <th>Kraj</th>
                  <th>Kategoria</th>
                  <th>Rocznik</th>
                  <th>Alkohol</th>
                  <th>Cena</th>
                  <th>Dodane przez</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product: Product) => (
                  <tr key={product.id} className="product-row">
                    <td className="product-name">
                      <div className="name-cell">
                        {product.image_url && (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="table-thumbnail"
                            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <div className="name-info">
                          <strong>{product.name}</strong>
                          {product.description_short && (
                            <div className="description-preview">
                              {product.description_short.length > 60 
                                ? `${product.description_short.substring(0, 60)}...`
                                : product.description_short
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${getTypeClassName(product.product_type_id)}`}>
                        {formatType(product.product_type_id)}
                      </span>
                    </td>
                    <td>{product.brand || '-'}</td>
                    <td>{product.country || '-'}</td>
                    <td>
                      <div className="category-info">
                        {product.category && <div>{product.category}</div>}
                        {product.subcategory && (
                          <div className="subcategory">{product.subcategory}</div>
                        )}
                      </div>
                    </td>
                    <td>{product.vintage || '-'}</td>
                    <td>
                      {product.alcohol_content ? `${product.alcohol_content}%` : '-'}
                    </td>
                    <td className="price-cell">
                      {formatPrice(product.price_net)}
                    </td>
                    <td className="user-cell">
                      <span className="added-by">
                        {product.username || 'Nieznany'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => navigate(`/product-editor/${product.id}`)}
                          className="btn btn-sm btn-primary"
                          title="Edytuj produkt"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => deleteProduct(product.id)}
                          className="btn btn-sm btn-danger"
                          title="Usuń produkt"
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductTable;
